import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import StackKit from "@/shared/components/kit/stack";
import { Box } from "@mui/material";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { ListMerchantData } from "@/types/api/merchant/merchant.type";
import BoxKit from "@/shared/components/kit/box";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import { useQuery } from "@tanstack/react-query";
import { fetchListGetBalancePazirande } from "@/api/merchant/request.get";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type ChartDataItem = {
  date: string;
  cache: string | number;
  credit: number;
};

type ColumnChartsProps = {
  categories?: string[];
  series?: any;
  barWidth?: string;
  colors?: string[];
  dataAssets?: ListMerchantData | undefined;
  chartData?: { hour: string; hourly_total: number }[];
  colorSideBar?: string;
  isSidebar?: boolean;
  setActiveTab?: any;
  activeTab?: string
  setSelected?: any
};
/**
 * ColumnCharts
 *
 * کامپوننت نمودار ستونی جهت نمایش داده‌های تراکنشی (روزانه، هفتگی، ماهانه)
 * با قابلیت استفاده در داشبورد، سایدبار، و محیط‌های واکنش‌گرا.
 *
 * این کامپوننت داده‌ها را از API دریافت کرده و بر اساس الگوهای مختلف زمانی،
 * آن‌ها را پردازش و در قالب نمودار ستونی نمایش می‌دهد. همچنین شامل بخش موجودی کیف پول
 * و تب‌های تغییر بازه زمانی است.
 *
 * @param categories آرایه دسته‌بندی‌ها برای نمودار (اختیاری)
 * @param series داده‌های سری‌های نمودار (اختیاری)
 * @param barWidth عرض هر ستون در نمودار (اختیاری)
 * @param colors رنگ‌های قابل تنظیم برای ستون‌ها (اختیاری)
 * @param dataAssets داده‌های تجمیعی تاجر (اختیاری)
 * @param chartData داده‌های خام نمودار شامل ساعت، روز یا تاریخ شمسی
 * @param colorSideBar رنگ متن محور y در حالت استفاده داخل سایدبار
 * @param isSidebar تعیین می‌کند که آیا کامپوننت برای نمایش سایدبار رندر شود یا خیر
 * @param setActiveTab تابع جهت تغییر تب فعال
 * @param activeTab تب فعال فعلی
 * @param setSelected تابع جهت تنظیم نوع بازه زمانی انتخاب‌شده
 *
 * @returns JSX.Element
 */
const ColumnCharts = (props: ColumnChartsProps) => {
  const { categories, series, barWidth = "55%", colors, dataAssets, chartData, colorSideBar, isSidebar, activeTab, setActiveTab, setSelected } = props;
  const chartRef = useRef<ChartJS<"bar", number[], string>>(null);

  const tabs = [{ id: 1, label: "روزانه", value: "daily" }, { id: 4, label: "هفتگی", value: "weekly" }, { id: 3, label: "ماهانه", value: "monthly" }];

  const createCacheGradient = ( ctx: CanvasRenderingContext2D, chartArea: any) => {
    const gradient = ctx.createLinearGradient(0,chartArea.top,0,chartArea.bottom);
    gradient.addColorStop(0, "#EB6440");
    gradient.addColorStop(1, "#853924");
    return gradient;
  };

  const createCreditGradient = ( ctx: CanvasRenderingContext2D, chartArea: any) => {
    const gradient = ctx.createLinearGradient(0,chartArea.top,0,chartArea.bottom);
    gradient.addColorStop(0, "#A1F5FF");
    gradient.addColorStop(1, "#619399");
    return gradient;
  };

const processChartData = (data: any[], activeTab?: string): ChartData<"bar", number[], string> => {
  if (!data || data.length === 0) return { labels: [], datasets: [] };

  let labels: string[] = [];
  let values: number[] = [];

  switch (activeTab) {
    case "daily":
      labels = data.map((item, index) =>
        item.date_shamsi ? toPersianNumber(item.date_shamsi) : `روز ${index + 1}`
      );
      values = data.map((item) => (item.cash || item.amount) ?? 0);
      break;

    case "weekly":
      labels = data.map((item, index) =>
        item.weekday || item.label || `هفته ${index + 1}`
      );
      values = data.map((item) => (item.cash || item.amount) ?? 0);
      break;

    case "monthly":
      labels = data.map((item, index) =>
        item.date_shamsi ? toPersianNumber(item.date_shamsi) : `ماه ${index + 1}`
      );
      values = data.map((item) => (item.cash || item.amount) ?? 0);
      break;

    default:
      labels = data.map((item, index) => {
        if ("hour" in item) return item.hour;
        if ("weekday" in item) return item.weekday || item.label || `هفته ${index + 1}`;
        if ("date_shamsi" in item) return toPersianNumber(item.date_shamsi);
        return `ردیف ${index + 1}`;
      });
      values = data.map((item) => (item.cash || item.amount || item.hourly_total) ?? 0);
      break;
  }

  return {
    labels,
    datasets: [
      {
        label: "تراکنش‌ها",
        data: values,
        backgroundColor: "#EB6440",
        borderColor: "#EB6440",
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false as const,
      },
    ],
  };
};


// Usage inside your component
const chartData_processed = processChartData(chartData || [], activeTab);


  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          usePointStyle: true,
          font: {
            family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
            size: 14,
          },
          color: "#757575",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        titleFont: {
          family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
          size: 14,
        },
        bodyFont: {
          family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
          size: 12,
        },
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            return `${context.dataset.label || ''}: ${value!.toLocaleString("fa-IR")} ریال`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
            size: 14,
          },
          color: "#757575",
        },

      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "",
          font: {
            family: "Arial, sans-serif",
            size: 14,
            weight: "bold",
          },
          color: colorSideBar ? colorSideBar : "#757575",
        },
        ticks: {
          font: {
            family: "Arial, sans-serif",
            size: 12,
          },
          precision: 0,
          color: colorSideBar ? colorSideBar : "#757575",
          callback: function (value) {
            return numberSeparator(value) + " ریال";
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
    datasets: {
      bar: {
        barPercentage: 0.3,
        categoryPercentage: 0.4,
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
  };

  const { data: dataBalancePazirande, isFetched, isLoading, } = useQuery({ queryKey: ["BalancePazirande-data"], queryFn: fetchListGetBalancePazirande, });

  return (
    <Box sx={{ direction: "rtl", width: "100%" }}>
      {/* Wallet Info Section */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <StackKit direction="column" justifyContent="center" width={'100%'} alignContent={'center'} gap={2}>
          {/* Wallet Balance */}
          <StackKit direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }}>
            <StackKit direction="row" justifyContent={{sm:'start',xs:'center'}} width={'100%'} alignItems="center" gap={1}>
              <EqualizerOutlinedIcon />
              <TypoGraphyKit variant="body1">
                موجودی کل کیف پول نقدی در حال حاضر
              </TypoGraphyKit>
            </StackKit>
            <StackKit direction={{ sm: "row", xs: 'column' }} justifyContent={{sm:'start',xs:'center'}} width={'100%'} alignItems="center" gap={2} sx={{ mt: { xs: 1, sm: 0 } }}>
              <TypoGraphyKit variant="subtitle1">
                {numberSeparator(dataBalancePazirande?.balance)} ریال
              </TypoGraphyKit>
            </StackKit>
          </StackKit>

          {/* Tabs */}
          <StackKit
            direction="row"
            width={'100%'}
            sx={{
              bgcolor: "#5A5A5A",
              padding: "10px",
              borderRadius: "8px",
              width: "100%",
              maxWidth:'250px',
              overflowX: "auto",
              overflowY: "hidden",
              whiteSpace: "nowrap",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" }, 
            }}
            gap={1}
          >
            {tabs.map((tab) => (
              <StackKit
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.label || '');
                  setSelected(tab.value);
                }}
                sx={{
                  padding: { xs: "6px 10px", sm: "6px 16px" },
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "8px",
                  bgcolor: activeTab === tab.label || '' ? "#434343" : "transparent",
                  color: activeTab === tab.label || '' ? "#fff" : "#B3B3B3",
                  border: activeTab === tab.label || '' ? "1px solid #434343" : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {bgcolor: activeTab === tab.label || '' ? "#434343" : "#666666",},
                  flexShrink: 0,
                }}
              >
                <TypoGraphyKit variant="body2" sx={{color: "inherit",fontSize: { xs: "0.75rem", sm: "0.85rem" },}}>
                  {tab.label || ''}
                </TypoGraphyKit>
              </StackKit>
            ))}
          </StackKit>

        </StackKit>
      </Box>

      {/* Chart Section */}
      <StackKit direction={{ xs: "column", md: "row" }} sx={{ width: "100%", overflowX: "auto", gap: { xs: 2, md: 3 } }}>
        {isSidebar && (
          <StackKit direction="column" sx={{ width: { xs: "100%", sm: "200px" }, minWidth: { xs: "100%", sm: "200px" }, borderRadius: "12px", justifyContent: "space-between", }} gap={1}>
            {[
              { color: "linear-gradient(180deg, #AF0000 0%, #490000 100%)", label: "اعتبار هدیه" },
              { color: "linear-gradient(180deg, #7B9D95 0%, #2B3734 100%)", label: "اعتبار داخلی" },
              { color: "linear-gradient(180deg, #7BC8B5 0%, #3C6259 100%)", label: "اعتبار خارجی" },
              { color: "linear-gradient(180deg, #EB6440 0%, #853924 100%)", label: "فروش نقدی خارجی" },
              { color: "linear-gradient(180deg, #F9C4B5 0%, #3E1105 100%)", label: "فروش نقدی داخلی" },
              { color: "linear-gradient(180deg, #A2F5FF 0%, #619399 100%)", label: "فروش IPG ( انلاین)" },
            ].map((item, idx) => (
              <StackKit key={idx} direction="row" alignItems="center" gap={1}>
                <BoxKit sx={{ width: "20px", height: "36px", borderRadius: "50px", background: item.color, flexShrink: 0, }} />
                <TypoGraphyKit variant="body1">{item.label}</TypoGraphyKit>
              </StackKit>
            ))}
          </StackKit>
        )}

        <Box sx={{ flex: 1, mt: { xs: 2, md: 0 }, height: { xs: "300px", md: "350px" }, minWidth: 0, boxShadow: "none", backgroundColor: "transparent", }}>
          <Bar ref={chartRef} data={chartData_processed} options={options} />
        </Box>
      </StackKit>
    </Box>

  );
};

export default ColumnCharts;
